import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Send, Stethoscope, RotateCcw, FileHeart, Download, Share2 } from "lucide-react";
import { downloadEvaluationPdf } from "../lib/pdf";
import { parseAssistant } from "../lib/api";
import { PhotoThumb, usePhotoPicker, AttachButton, PendingPhotos } from "./ChatPhotos";

export const DOCTOR_IMG = "/doctor-avatar.webp";

const Bubble = ({ m, i, cid }) => {
  const mine = m.role === "user";
  const text = mine ? m.content : parseAssistant(m.content).clean;
  const photos = m.photos || [];
  if (!text && !photos.length) return null;
  return (
    <div className={`fade-up flex flex-col ${mine ? "items-end" : "items-start"} gap-1.5`} data-testid={`chat-msg-${i}`}>
      {photos.length > 0 && <div className="flex gap-1.5">{photos.map((p) => <PhotoThumb key={p} cid={cid} pid={p} />)}</div>}
      {text && (
        <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${mine ? "rounded-br-sm bg-teal-700 text-white" : "rounded-bl-sm bg-slate-100 text-slate-800"}`}>
          {text}
        </div>
      )}
    </div>
  );
};

export const DoctorChat = ({ t, consult, messages, streaming, canDiagnose, diagnosis, onSend, onDiagnose, onReset }) => {
  const [text, setText] = useState("");
  const endRef = useRef(null);
  const visible = messages.filter((m) => !m.hidden);
  const last = visible[visible.length - 1];
  const options = !streaming && last?.role === "assistant" && !diagnosis ? parseAssistant(last.content).options : [];
  const picker = usePhotoPicker(consult.id, messages.reduce((n, m) => n + (m.photos?.length || 0), 0), t);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, diagnosis, picker.pending.length]);
  const send = (v) => {
    if ((!v.trim() && !picker.pending.length) || streaming || picker.busy) return;
    onSend(v.trim(), picker.pending); setText(""); picker.clear();
  };

  return (
    <div className="notranslate flex h-full min-h-0 flex-col" translate="no" data-testid="doctor-chat">
      <div className="flex items-center gap-3 border-b border-slate-100 pb-4 pr-8">
        <img src={DOCTOR_IMG} alt="Dr. Nova" className="h-11 w-11 rounded-full object-cover object-top" />
        <div className="min-w-0 flex-1">
          <p className="font-display truncate font-bold text-slate-900">{t("doctor")}</p>
          <p className="truncate text-xs font-medium text-orange-700" data-testid="chat-zone-label">{consult.zone === "general" ? consult.zone_label : `${consult.zone_label} · ${consult.subzone_label}`}</p>
        </div>
        <button data-testid="new-consultation-btn" onClick={onReset} className="icon-btn !h-9 !px-3" title={t("newConsult")}><RotateCcw size={15} /></button>
      </div>

      <div className="scroll-thin flex-1 space-y-3 overflow-y-auto py-4" data-testid="chat-messages">
        {visible.map((m, i) => <Bubble key={i} m={m} i={i} cid={consult.id} />)}
        {streaming && diagnosis === null && (last?.role !== "assistant" || !last.content) && (
          <p className="flex items-center gap-2 text-xs text-teal-700" data-testid="typing-indicator"><Stethoscope size={14} className="animate-pulse" />{t("typing")}</p>
        )}
        {diagnosis !== null && (
          <div className="fade-up rounded-2xl border border-teal-100 bg-teal-50/60 p-5" data-testid="diagnosis-card">
            <p className="label flex items-center gap-2"><FileHeart size={14} />{t("diagnosisTitle")}</p>
            <div className="prose-clinic mt-3 text-sm">
              {diagnosis ? <ReactMarkdown>{diagnosis}</ReactMarkdown> : <p className="animate-pulse text-slate-500">{t("adPreparing")}</p>}
            </div>
            {diagnosis && !streaming && (
              <button data-testid="download-pdf-btn" onClick={() => downloadEvaluationPdf({ t, consult, messages, diagnosis })} className="btn-primary mt-4 w-full !py-2.5">
                <Download size={16} />{t("downloadPdf")}
              </button>
            )}
            {diagnosis && !streaming && (
              <a data-testid="share-whatsapp-btn" target="_blank" rel="noopener noreferrer"
                href={`https://wa.me/?text=${encodeURIComponent(`${t("shareText")} ${window.location.origin}`)}`}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.02]">
                <Share2 size={16} />{t("shareWhatsapp")}
              </a>
            )}
          </div>
        )}
        <div ref={endRef} />
      </div>

      {diagnosis === null && (
        <div className="space-y-3 border-t border-slate-100 pt-4">
          {options.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="quick-options">
              {options.map((o, i) => (
                <button key={o} data-testid={`quick-option-${i}`} onClick={() => send(o)}
                  className="rounded-full border border-teal-200 bg-white px-3.5 py-2 text-sm text-teal-800 transition-colors hover:bg-teal-50">{o}</button>
              ))}
            </div>
          )}
          {canDiagnose && (
            <button data-testid="get-diagnosis-btn" disabled={streaming} onClick={onDiagnose} className="btn-accent w-full">
              <FileHeart size={18} /> {t("getDiagnosis")}
            </button>
          )}
          <PendingPhotos t={t} cid={consult.id} picker={picker} />
          <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="flex gap-2">
            <AttachButton t={t} picker={picker} disabled={streaming} />
            <input data-testid="chat-input" maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("placeholder")}
              onFocus={() => setTimeout(() => endRef.current?.scrollIntoView({ block: "end" }), 350)}
              className="min-w-0 flex-1 rounded-full border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-teal-600" />
            <button data-testid="chat-send-btn" type="submit" disabled={streaming || picker.busy || (!text.trim() && !picker.pending.length)} className="grid h-11 w-11 place-items-center rounded-full bg-teal-700 text-white transition-transform hover:scale-105 disabled:opacity-40"><Send size={16} /></button>
          </form>
        </div>
      )}
    </div>
  );
};
